import { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form } from 'antd';
import Content from 'layouts/Content';
import { InputForm, SelectForm, Button } from 'components';
import ButtonFilter from 'components/Button/ButtonFilter';
import ButtonExport from 'components/Button/ButtonExport';
import DatePickerForm from 'components/DatePicker/DatePickerForm';
import TableComponent from 'components/Table/TableComponent';
import { useMutation } from '@tanstack/react-query';
import DebtApis from 'apis/DebtApis';
import { useTitle } from 'contexts/TitleProvider';
import { PlantApi } from 'apis/PlantApis';
import { CustomerApis } from 'apis/CustomerApis';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import LazySelectForm, {
  initLazySelectData,
  ILazySelectData,
  ILazySelectParams,
} from 'components/Select/LazySelectForm';
import { DefaultOptionType } from 'antd/es/select';
import { useUser } from 'contexts/UserProvider';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { EyeIcon } from 'assets/svg';
import ButtonAction from 'components/Button/ButtonAction';
import { useNavigate } from 'react-router-dom';
import path from 'routes/Path';
import dayjs from 'dayjs';
import formatNumber from 'utils/constants/functions';

const DEFAULT_PAGE_SIZE = 10;

export default function DebtDMSPage() {
  const { setTitle, setBreadcrumb } = useTitle();
  const { user } = useUser();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [openFilter, setOpenFilter] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [pageInfo, setPageInfo] = useState({
    currentPage: 1,
    itemsPerPage: DEFAULT_PAGE_SIZE,
    totalItems: 0,
  });
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({
    plantId: '',
    type: '',
    sapCustomerId: '',
  });
  const [plantOptions, setPlantOptions] = useState<DefaultOptionType[]>([]);
  const [customerOptions, setCustomerOptions] =
    useState<ILazySelectData>(initLazySelectData);
  const [teamOptions, setTeamOptions] =
    useState<ILazySelectData>(initLazySelectData);
  const [loadingPlant, setLoadingPlant] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [loadingTeam, setLoadingTeam] = useState(false);

  const { mutate: fetchData } = useMutation({
    mutationFn: async (params: any) => {
      const take = params.pageSize || pageSize || DEFAULT_PAGE_SIZE;
      const input: any = {
        plantId: params.plantId || null,
        teamSaleId: params.teamSaleId || null,
        sapCustomerId: params.sapCustomerId || null,
        comparison: params.type ? (params.type == 'true' ? true : false) : null,
        effectiveTimeFrom: params.fromDate || null,
        effectiveTimeTo: params.toDate || null,
        skip: (params.page - 1) * take,
        take,
      };
      const res = await DebtApis.getCustomerDebitComparisonSummary(input);
      return res?.data?.getCustomerDebitComparisonSummary || {};
    },
    onSuccess: (result: any, variables: any) => {
      const page = variables.page || 1;
      const take = variables.pageSize || pageSize || DEFAULT_PAGE_SIZE;
      const items = result?.items || [];
      const filteredItems = items.filter(
        (it: any) =>
          !(
            it?.teamName === 'TỔNG CỘNG' ||
            (it?.plantId == null && it?.teamSaleId == null)
          ),
      );
      const mapped = filteredItems.map((it: any, idx: number) => ({
        _key: `${page}-${take}-${idx}-${it.plantId || 'TOTAL'}-${it.teamSaleId || 'NA'}`,
        id: `${it.plantId || 'TOTAL'}-${it.teamSaleId || idx}`,
        stt: (page - 1) * take + idx + 1,
        plantId: it.plantId,
        unitCode: it.teamSaleId,
        unit: it.teamName,
        compare: it.comparisonYn === true ? 'Khớp' : 'Lệch',
        cnpt_slkh: it.quantityCustomerDebit,
        cnpt_q10: it.dmsCollectDebitQ10,
        cnpt_tienmat: it.dmsCollectDebitAmount,
        hm_slkh: it.quantityRuleCustomerDebit,
        hm_q10: it.goldThreadQ10Traditional,
        hm_tienmat: it.amountWagesTraditional,
        cnvhm_slkh: it.quantityExceedLimit,
        cnvhm_q10: it.q10ExceedLimit,
        cnvhm_tienmat: it.amountExceedLimit,
        ptkh_q10: it.dmsPayDebitQ10,
        ptkh_tienmat: it.dmsPayDebitAmount,
        q10_gh: it.pnjPayDebitQ10,
        currency: it.unitPrice,
        q10_unit: it.unitQ10,
        updatedAt: it.modifyDate
          ? dayjs(it.modifyDate).format('DD/MM/YYYY HH:mm:ss')
          : '-',
      }));
      setData(mapped);
      setPageInfo({
        currentPage: result?.pageInfo?.currentPage || page,
        itemsPerPage: result?.pageInfo?.itemsPerPage || take,
        totalItems: result?.pageInfo?.totalItems || mapped.length,
      });
      setLoading(false);
    },
    onMutate: () => setLoading(true),
    onSettled: () => setLoading(false),
  });

  const handleSearch = (values: any) => {
    setFilter(values);
    fetchData({ ...values, page: 1 });
  };
  const handleCancel = () => {
    form.resetFields();
    setFilter({ plantId: '', type: '', sapCustomerId: '' });
    fetchData({ page: 1 });
  };

  const handleTableChange = (pagination: any) => {
    if (pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      fetchData({ ...filter, page: 1, pageSize: pagination.pageSize });
    } else {
      fetchData({ ...filter, page: pagination.current, pageSize });
    }
  };

  // Lấy danh sách chi nhánh
  useEffect(() => {
    setLoadingPlant(true);
    PlantApi.getAllPlantFilterHO()
      .then((res: any) => {
        const items = res?.data?.getPlant?.items || [];
        // Loại bỏ 5000 (hội sở)
        const filtered = items.filter((item: any) => item.id !== '5000');
        setPlantOptions(
          filtered.map((item: any) => ({
            label: item.id + (item.name ? ` - ${item.name}` : ''),
            value: item.id,
          })),
        );
        setLoadingPlant(false);
        // Mặc định chọn chi nhánh theo user
        if (
          user?.plantCode &&
          filtered.some((item: any) => item.id === user.plantCode)
        ) {
          form.setFieldValue('plantId', user.plantCode);
        }
      })
      .catch(() => setLoadingPlant(false));
  }, [user, form]);

  // Lấy danh sách khách hàng khi search
  const handleScrollCustomersChange = useCallback(
    (params: ILazySelectParams) => {
      setLoadingCustomer(true);
      const plantId = form.getFieldValue('plantId');
      CustomerApis.getCustomersInCombobox({
        pageSize: params.pageSize,
        searchValue: params.value || '',
        plantId,
      })
        .then((res: any) => {
          const data = res?.data?.getCustomer;
          setCustomerOptions({
            options:
              data?.items?.map((item: any) => ({
                label:
                  (item?.sapCustomerId || item?.id) +
                  (item?.storeName ? ` - ${item.storeName}` : ''),
                value: item?.sapCustomerId,
              })) || [],
            totalItems: data?.pageInfo?.totalItems || 0,
          });
          setLoadingCustomer(false);
        })
        .catch(() => setLoadingCustomer(false));
    },
    [form],
  );

  // Lấy danh sách đội khi search
  const handleTeamSelectScrollChange = useCallback(
    (params: ILazySelectParams) => {
      setLoadingTeam(true);
      SaleTeamApi.getAllTeamSaleCb(params as any)
        .then((res: any) => {
          const data = res?.data?.getTeamSales;
          setTeamOptions({
            options:
              data?.items?.map((item: any) => ({
                label: `${item?.subId} - ${item?.name}`,
                value: item?.id,
              })) || [],
            totalItems: data?.pageInfo?.totalItems || 0,
          });
          setLoadingTeam(false);
        })
        .catch(() => setLoadingTeam(false));
    },
    [],
  );

  // Khi đổi chi nhánh thì reset sapCustomerId
  const handleChangePlant = (value: any) => {
    form.setFieldValue('sapCustomerId', undefined);
    setCustomerOptions(initLazySelectData);
  };

  useEffect(() => {
    setTitle('Công nợ DMS');
    setBreadcrumb([{ title: 'Kế toán/Thủ quỹ' }, { title: 'Công nợ DMS' }]);
    fetchData({ ...filter, page: 1 });
    // eslint-disable-next-line
  }, []);

  const columns = [
    {
      title: 'STT',
      dataIndex: 'stt',
      width: 60,
      align: 'center' as const,
      render: (_: any, __: any, idx: number) =>
        (pageInfo.currentPage - 1) * pageSize + idx + 1,
    },
    {
      title: 'Chi nhánh',
      dataIndex: 'plantId',
      width: 100,
      align: 'center' as const,
    },
    {
      title: 'Mã đơn vị',
      dataIndex: 'unitCode',
      width: 100,
      align: 'center' as const,
    },
    {
      title: 'Đơn vị',
      dataIndex: 'unit',
      width: 160,
      align: 'center' as const,
    },
    {
      title: 'So sánh',
      dataIndex: 'compare',
      width: 100,
      align: 'center' as const,
      render: (v: any) =>
        v === 'Lệch' ? <span style={{ color: 'red' }}>Lệch</span> : v,
    },
    {
      title: 'Công nợ phải thu',
      align: 'center' as const,
      children: [
        {
          title: 'SL KH',
          dataIndex: 'cnpt_slkh',
          width: 80,
          align: 'right' as const,
          render: (v: any) => formatNumber(v) || '-',
        },
        {
          title: 'Q10',
          dataIndex: 'cnpt_q10',
          width: 80,
          align: 'right' as const,
          render: (v: any) => formatNumber(v) || '-',
        },
        {
          title: 'VND',
          dataIndex: 'cnpt_tienmat',
          width: 140,
          align: 'right' as const,
          render: (v: any) => formatNumber(v) || '-',
        },
      ],
    },
    {
      title: 'Hạn mức',
      align: 'center' as const,
      children: [
        {
          title: 'SL KH',
          dataIndex: 'hm_slkh',
          width: 80,
          align: 'right' as const,
          render: (v: any) => formatNumber(v) || '-',
        },
        {
          title: 'Q10',
          dataIndex: 'hm_q10',
          width: 80,
          align: 'right' as const,
          render: (v: any) => formatNumber(v) || '-',
        },
        {
          title: 'VND',
          dataIndex: 'hm_tienmat',
          width: 140,
          align: 'right' as const,
          render: (v: any) => formatNumber(v) || '-',
        },
      ],
    },
    {
      title: 'Công nợ vượt hạn mức',
      align: 'center' as const,
      children: [
        {
          title: 'SL KH',
          dataIndex: 'cnvhm_slkh',
          width: 80,
          align: 'right' as const,
          render: (v: any) => formatNumber(v) || '-',
        },
        {
          title: 'Q10',
          dataIndex: 'cnvhm_q10',
          width: 80,
          align: 'right' as const,
          render: (v: any) => formatNumber(v) || '-',
        },
        {
          title: 'VND',
          dataIndex: 'cnvhm_tienmat',
          width: 140,
          align: 'right' as const,
          render: (v: any) => formatNumber(v) || '-',
        },
      ],
    },
    {
      title: 'Phải trả KH',
      align: 'center' as const,
      children: [
        {
          title: 'Q10',
          dataIndex: 'ptkh_q10',
          width: 80,
          align: 'right' as const,
          render: (v: any) => formatNumber(v) || '-',
        },
        {
          title: 'VND',
          dataIndex: 'ptkh_tienmat',
          width: 140,
          align: 'right' as const,
          render: (v: any) => formatNumber(v) || '-',
        },
      ],
    },
    {
      title: 'Nguyên liệu giữ hộ khách: Q10',
      dataIndex: 'q10_gh',
      width: 100,
      align: 'center' as const,
    },
    {
      title: 'Tiền tệ',
      dataIndex: 'currency',
      width: 80,
      align: 'center' as const,
    },
    {
      title: 'Đơn vị Q10',
      dataIndex: 'q10_unit',
      width: 80,
      align: 'center' as const,
    },
    {
      title: 'Ngày cập nhật',
      dataIndex: 'updatedAt',
      width: 160,
      align: 'center' as const,
    },
    {
      title: '',
      dataIndex: 'action',
      width: 80,
      fixed: 'right' as const,
      align: 'center' as const,
      render: (_: any, record: any) => (
        <ButtonAction
          tooltip="Xem chi tiết"
          onClick={() =>
            navigate(path.DEBT_DMS_DETAIL, {
              state: {
                plantId: record?.plantId,
                teamSaleId: record?.unitCode,
              },
            })
          }
        >
          <EyeIcon />
        </ButtonAction>
      ),
    },
  ];

  const handleExportExcel = () => {
    const exportData = data.map((item: any) => ({ ...item }));
    exportExcel_v2(columns, exportData, 'CongNoDMS');
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div></div>
        <div className="flex gap-2">
          <ButtonFilter isOpen={openFilter} setIsOpen={setOpenFilter} />
          <ButtonExport onClick={handleExportExcel} />
          <Button title="Cập nhật" type="button" displayType="approve" />
        </div>
      </div>
      {openFilter && (
        <Content>
          <Form form={form} layout="vertical" onFinish={handleSearch}>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Form.Item name="plantId" label="Chi nhánh">
                  <SelectForm
                    options={plantOptions}
                    loading={loadingPlant}
                    placeholder="Chọn chi nhánh"
                    allowClear
                    disabled={user?.plantCode !== '5000'}
                    onChange={handleChangePlant}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="teamSaleId" label="Đội">
                  <LazySelectForm
                    placeholder="Chọn đội"
                    data={teamOptions}
                    loading={loadingTeam}
                    onSelectScrollChange={handleTeamSelectScrollChange}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="sapCustomerId" label="Khách hàng">
                  <LazySelectForm
                    placeholder="Mã khách hàng"
                    data={customerOptions}
                    loading={loadingCustomer}
                    onSelectScrollChange={handleScrollCustomersChange}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="type" label="So sánh SAP">
                  <SelectForm
                    options={[
                      { label: 'Khớp', value: 'true' },
                      { label: 'Lệch', value: 'false' },
                    ]}
                    placeholder="Chọn So sánh SAP"
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Form.Item name="fromDate" label="Từ ngày">
                  <DatePickerForm
                    className="w-full"
                    placeholder="dd/mm/yyyy"
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="toDate" label="Đến ngày">
                  <DatePickerForm
                    className="w-full"
                    placeholder="dd/mm/yyyy"
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>{/* Cột trống để căn chỉnh */}</Col>
            </Row>
            <Row gutter={[16, 16]} justify="end">
              <Col span={18}>{/* Cột trống để căn chỉnh */}</Col>
              <Col span={6}>
                <div className="flex gap-2 justify-end">
                  <Button
                    title="Hủy"
                    type="button"
                    handleOnclick={handleCancel}
                  />
                  <Button fill title="Tìm kiếm" type="submit" />
                </div>
              </Col>
            </Row>
          </Form>
        </Content>
      )}
      <Content>
        <TableComponent
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{
            current: pageInfo.currentPage,
            pageSize: pageSize,
            total: pageInfo.totalItems,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            onChange: (page, pageSize) =>
              handleTableChange({ current: page, pageSize }),
          }}
          bordered
          rowKey="_key"
          scroll={{ x: 2000 }}
        />
      </Content>
    </>
  );
}
